import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { Link, Navigate, useParams } from '@/utils/router';
import { BUSINESS_BASE, SEO_SERVICE_HENNA } from '@/constants/core';
import { ICONS } from '@/constants/icons';
import {
  getPlanningArticleBySlug,
  getPlanningArticlePath,
  getPlanningArticleParentHref,
} from '@/constants/event-planning';
import { findRegionByCity } from '@/constants/geo';
import {
  HEADER_HEIGHT,
  MEDIA_BREAKPOINTS,
  SEPARATOR_HEIGHT,
} from '@/constants/layout';
import Footer from '@/components/Footer/Footer';
import MenuDesktop from '@/components/MenuDesktop/MenuDesktop';
import MenuMobile from '@/components/MenuMobile/MenuMobile';
import { useI18n } from '@/i18n/LanguageProvider';
import { useTheme } from '@/theme/ThemeProvider';
import type { ThemeMode } from '@/theme/themes';
import {
  getPlanningPageBackground,
  getPlanningPageTheme,
  getPlanningScrollbarColor,
  getPlanningScrollbarThumb,
  getPlanningScrollbarThumbHover,
  getPlanningScrollbarTrack,
  type PlanningThemeVariant,
} from '@/pages/planningTheme';
import { scheduleSpaReadyAfterPaint } from '@/utils/spaReady';

const PLANNING_ART_LOGO_URL = '/images/logo/2.png';
type PageDirection = 'rtl' | 'ltr';
type PlanningArticleMenuLabels = {
  intro: string;
  essentials: string;
  contact: string;
  trust: string;
  work: string;
  related: string;
  faq: string;
  author: string;
};
type PlanningArticleSectionId =
  | 'planning-article-intro'
  | 'planning-article-essentials'
  | 'planning-article-contact'
  | 'planning-article-trust'
  | 'planning-article-work'
  | 'planning-article-related'
  | 'planning-article-faq'
  | 'planning-article-author';

const PLANNING_ARTICLE_INTRO_SECTION_ID = 'planning-article-intro';
const PLANNING_ARTICLE_DEFAULT_MENU_SECTION_ID =
  PLANNING_ARTICLE_INTRO_SECTION_ID;
const mediumScreenBreakpoint = parseInt(MEDIA_BREAKPOINTS.mediumScreen, 10);

const normalizeSlugParam = (value: string | undefined) => {
  const raw = String(value ?? '').trim();
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
};

const PlanningGlobalStyle = createGlobalStyle<{
  $mode: ThemeMode;
  $variant?: PlanningThemeVariant;
}>`
  html {
    scrollbar-color: ${({ $mode, $variant }) =>
      getPlanningScrollbarColor($mode, $variant)};
  }

  body,
  #root {
    background: ${({ $mode, $variant }) =>
      getPlanningPageBackground($mode, $variant)};
  }

  ::-webkit-scrollbar-track {
    background: ${({ $mode, $variant }) =>
      getPlanningScrollbarTrack($mode, $variant)};
  }

  ::-webkit-scrollbar-thumb {
    background: ${({ $mode, $variant }) =>
      getPlanningScrollbarThumb($mode, $variant)};
    border: 3px solid
      ${({ $mode, $variant }) => getPlanningScrollbarTrack($mode, $variant)};
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${({ $mode, $variant }) =>
      getPlanningScrollbarThumbHover($mode, $variant)};
  }
`;

const Page = styled.div<{
  $dir: PageDirection;
  $mode: ThemeMode;
  $variant?: PlanningThemeVariant;
}>`
  min-height: 100vh;
  overflow-x: hidden;
  direction: ${({ $dir }) => $dir};
  color: var(--planning-ink);
  font-family: ${({ theme }) => theme.fonts.rubikBoldItalic};
  transition:
    background 0.25s ease,
    color 0.25s ease;

  ${({ $mode, $variant }) => getPlanningPageTheme($mode, $variant)}

  background: var(--planning-page-bg);
`;

const Main = styled.main`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  max-width: 1040px;
  margin: 0 auto;
  padding: ${HEADER_HEIGHT + 28}px 22px 72px;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    padding: 18px 14px 48px;
  }
`;

const Hero = styled.article`
  display: grid;
  min-width: 0;
  grid-template-columns: minmax(0, 0.78fr) minmax(420px, 1.22fr);
  gap: 20px;
  align-items: stretch;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    grid-template-columns: 1fr;
  }
`;

const HeroText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  border: 1px solid var(--planning-border);
  border-radius: 8px;
  padding: clamp(18px, 2.6vw, 26px);
  background: var(--planning-card);
  backdrop-filter: blur(12px);
  box-shadow: 0 24px 58px var(--planning-shadow-strong);

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    padding: 12px 12px 16px;
  }
`;

const Eyebrow = styled.p`
  margin: 0 0 8px;
  color: var(--planning-accent-soft);
  font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
  font-weight: 800;
  font-size: clamp(1.08rem, 1.85vw, 1.35rem);
  line-height: 1.25;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    margin-bottom: 6px;
  }
`;

const Title = styled.h1`
  margin: 0;
  max-width: 720px;
  font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
  font-size: clamp(1.7rem, 3vw, 2.45rem);
  line-height: 1.12;
  letter-spacing: 0;
  color: var(--planning-heading);
`;

const Lead = styled.p`
  margin: 16px 0 0;
  color: var(--planning-muted);
  font-size: clamp(1rem, 1.55vw, 1.12rem);
  line-height: 1.62;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    margin-top: 10px;
    line-height: 1.5;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  box-sizing: border-box;
  aspect-ratio: 16 / 10;
  height: 100%;
  object-fit: contain;
  object-position: center;
  border-radius: 8px;
  border: 1px solid var(--planning-border);
  background: var(--planning-panel);
  box-shadow: 0 24px 58px var(--planning-shadow-strong);
`;

const Body = styled.section`
  margin-top: 18px;
  display: grid;
  min-width: 0;
  grid-template-columns: minmax(0, 1fr) minmax(250px, 0.42fr);
  grid-template-rows: auto auto auto;
  gap: 18px;
  align-items: stretch;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    grid-template-columns: 1fr;
    grid-template-rows: none;
  }
`;

const Panel = styled.section`
  min-width: 0;
  box-sizing: border-box;
  border: 1px solid var(--planning-border);
  border-radius: 8px;
  padding: clamp(22px, 3vw, 30px);
  background: var(--planning-card);
  backdrop-filter: blur(12px);
  box-shadow: 0 18px 42px var(--planning-shadow);

  h2 {
    margin: 0 0 14px;
    font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
    font-size: clamp(1.3rem, 2.6vw, 1.75rem);
    color: var(--planning-accent-soft);
  }

  p,
  li {
    color: var(--planning-muted);
    line-height: 1.78;
    font-size: 1rem;
  }

  p {
    margin: 0;
  }

  ul {
    margin: 0;
    padding: 0;
    list-style: none;
    display: grid;
    gap: 10px;
  }

  li {
    position: relative;
    padding-inline-start: 28px;
  }

  li::before {
    content: '';
    position: absolute;
    inset-inline-start: 0;
    top: 0.72em;
    width: 10px;
    height: 10px;
    border-radius: 999px;
    background: var(--planning-accent);
    box-shadow: 0 0 0 4px var(--planning-button-bg);
  }
`;

const PrimaryPanel = styled(Panel)`
  grid-column: 1;
  grid-row: 1;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    grid-column: 1;
    grid-row: auto;
  }
`;

const ParentContextText = styled.p`
  margin-top: 18px;

  a {
    color: var(--planning-accent-soft);
    font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
    text-decoration: underline;
    text-underline-offset: 5px;
  }

  a:focus-visible {
    outline: 2px solid var(--planning-border-strong);
    outline-offset: 4px;
  }
`;

const AuthorLinkText = styled(ParentContextText)`
  && {
    margin-top: 14px;
  }
`;

const TrustPanel = styled(Panel)`
  grid-column: 1;
  grid-row: 2;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    grid-column: 1;
    grid-row: auto;
  }
`;

const WorkPanel = styled(Panel)`
  grid-column: 1;
  grid-row: 3;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    grid-column: 1;
    grid-row: auto;
  }
`;

const FullWidthPanel = styled(Panel)`
  grid-column: 1 / -1;
  min-width: 0;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    grid-column: 1;
  }
`;

const RelatedPanel = styled(FullWidthPanel)`
  background: var(--planning-card-strong);
  border-color: var(--planning-border-strong);
  box-shadow: 0 22px 54px var(--planning-shadow-strong);

  h2 {
    color: var(--planning-accent-soft);
  }
`;

const FaqPanel = styled(FullWidthPanel)`
  background: var(--planning-card-strong);
  border-color: var(--planning-border-strong);
  box-shadow: 0 18px 42px var(--planning-shadow);

  h2 {
    color: var(--planning-accent-soft);
  }
`;

const RelatedHeader = styled.div`
  margin-bottom: 8px;
`;

const RelatedCarousel = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 18px 58px;
  border: 1px solid var(--planning-border);
  border-radius: 8px;
  background: var(--planning-panel);
  backdrop-filter: blur(10px);
  overflow: hidden;
  isolation: isolate;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    padding: 14px 52px;
  }
`;

const RelatedControl = styled.button<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${({ $side }) => $side}: 4px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  box-sizing: border-box;
  transform: translateY(-50%);
  border: 1px solid var(--planning-border-strong);
  border-radius: 999px;
  background: var(--planning-panel-strong);
  color: var(--planning-accent-soft);
  font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
  font-size: 1.35rem;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 12px 24px var(--planning-shadow);

  &:disabled {
    cursor: not-allowed;
    opacity: 0.35;
  }

  &:not(:disabled):hover,
  &:not(:disabled):focus-visible {
    background: var(--planning-card-hover);
    border-color: var(--planning-border-strong);
    color: var(--planning-heading);
    outline: 2px solid var(--planning-border-strong);
    outline-offset: 3px;
  }

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    ${({ $side }) => $side}: 5px;
    width: 40px;
    height: 40px;
  }
`;

const RelatedCardsGrid = styled.div<{ $visible: number; $dir: PageDirection }>`
  direction: ${({ $dir }) => $dir};
  display: grid;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  grid-template-columns: ${({ $visible }) =>
    `repeat(${$visible}, minmax(0, 1fr))`};
  align-items: stretch;
  gap: 14px;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    grid-template-columns: 1fr;
  }
`;

const RelatedCard = styled(Link)<{ $dir: PageDirection }>`
  display: grid;
  grid-template-rows: auto auto 1fr;
  gap: 7px;
  width: 100%;
  min-width: 0;
  height: 390px;
  min-height: 390px;
  box-sizing: border-box;
  overflow: hidden;
  border-radius: 8px;
  padding: 12px;
  color: var(--planning-ink);
  text-decoration: none;
  direction: ${({ $dir }) => $dir};
  background: var(--planning-card);
  border: 1px solid var(--planning-border);
  backdrop-filter: blur(10px);
  box-shadow: 0 16px 34px var(--planning-shadow);

  &:hover,
  &:focus-visible {
    background: var(--planning-card-hover);
    border-color: var(--planning-border-strong);
    outline: 2px solid var(--planning-border-strong);
    outline-offset: 3px;
  }

  img {
    width: 100%;
    box-sizing: border-box;
    aspect-ratio: 16 / 10;
    object-fit: cover;
    border-radius: 6px;
  }

  strong {
    font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
    color: var(--planning-accent-soft);
    line-height: 1.28;
    display: -webkit-box;
    overflow: hidden;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  span {
    color: var(--planning-muted);
    line-height: 1.48;
    font-size: 0.94rem;
    display: -webkit-box;
    max-height: calc(1.48em * 6);
    overflow: hidden;
    text-overflow: ellipsis;
    overflow-wrap: anywhere;
    -webkit-line-clamp: 6;
    -webkit-box-orient: vertical;
  }

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    grid-template-rows: auto auto auto;
    height: auto;
    min-height: 0;
    gap: 8px;

    span {
      max-height: calc(1.48em * 6);
      -webkit-line-clamp: 6;
    }
  }
`;

const FaqList = styled.div`
  display: grid;
  gap: 12px;
`;

const FaqItem = styled.details<{ $dir: PageDirection }>`
  direction: ${({ $dir }) => $dir};
  border-radius: 8px;
  padding: 0;
  overflow: hidden;
  background: var(--planning-panel);
  border: 1px solid var(--planning-border);
  backdrop-filter: blur(10px);
  transition:
    background 180ms ease,
    border-color 180ms ease;

  summary {
    direction: ${({ $dir }) => $dir};
    text-align: start;
    cursor: pointer;
    font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
    line-height: 1.45;
    list-style: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    padding: 14px 16px;
    color: var(--planning-accent-soft);
  }

  summary::-webkit-details-marker {
    display: none;
  }

  summary:focus-visible {
    outline: 2px solid var(--planning-border-strong);
    outline-offset: -4px;
  }

  summary::after {
    content: '';
    flex: 0 0 auto;
    display: block;
    width: 24px;
    height: 24px;
    border-radius: 999px;
    background: var(--planning-button-bg);
    color: var(--planning-accent-soft);
    background-image:
      linear-gradient(45deg, transparent 50%, currentColor 50%),
      linear-gradient(135deg, currentColor 50%, transparent 50%);
    background-position:
      calc(50% - 4px) 52%,
      calc(50% + 4px) 52%;
    background-size:
      7px 7px,
      7px 7px;
    background-repeat: no-repeat;
    transform: rotate(0deg);
    transition:
      transform 220ms ease,
      background-color 180ms ease;
  }

  p {
    direction: ${({ $dir }) => $dir};
    text-align: start;
    margin: 0;
    max-height: 0;
    color: var(--planning-muted);
    overflow: hidden;
    padding: 0 16px;
    opacity: 0;
    transform: translateY(-4px);
    transition:
      max-height 280ms ease,
      opacity 220ms ease,
      transform 220ms ease,
      padding 220ms ease;
  }

  &[open] {
    background: var(--planning-card-hover);
    border-color: var(--planning-border-strong);
  }

  &[open] summary::after {
    transform: rotate(180deg);
    background-color: var(--planning-button-bg);
  }

  &[open] p {
    max-height: 260px;
    padding: 0 16px 16px;
    opacity: 1;
    transform: translateY(0);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    summary::after,
    p {
      transition: none;
    }
  }
`;

const CtaPanel = styled.aside`
  grid-column: 2;
  grid-row: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  border-radius: 8px;
  padding: 24px;
  border: 1px solid var(--planning-border-strong);
  background: var(--planning-panel-strong);
  backdrop-filter: blur(12px);
  color: var(--planning-ink);
  text-align: center;
  box-shadow: 0 22px 55px var(--planning-shadow-strong);

  h2 {
    margin: 0 0 10px;
    font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
    color: var(--planning-accent-soft);
    font-size: 1.22rem;
  }

  p {
    margin: 0 0 18px;
    color: var(--planning-muted);
    line-height: 1.68;
  }

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    grid-column: 1;
    grid-row: auto;
  }
`;

const ActionRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  width: min(100%, 230px);
  margin: 0 auto;
  justify-content: stretch;
  gap: 10px;
`;

const CtaButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
  min-height: 48px;
  border-radius: 999px;
  padding: 10px 14px;
  background: var(--planning-accent);
  border: 1px solid var(--planning-border-strong);
  color: var(--planning-on-accent);
  font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
  font-weight: 900;
  font-size: 0.95rem;
  text-decoration: none;
  white-space: nowrap;
  box-shadow: 0 12px 24px var(--planning-shadow);
  transition:
    transform 160ms ease,
    background 180ms ease,
    border-color 180ms ease,
    box-shadow 180ms ease;

  &:hover,
  &:focus-visible {
    background: var(--planning-button-hover-bg);
    border-color: var(--planning-border-strong);
    color: var(--planning-button-hover-ink);
    transform: translateY(-1px);
    box-shadow: 0 15px 28px var(--planning-shadow-strong);
    outline: 2px solid var(--planning-border-strong);
    outline-offset: 3px;
  }

  svg {
    flex: 0 0 auto;
    width: 1em;
    height: 1em;
    padding: 5px;
    border-radius: 999px;
    background: var(--planning-button-bg);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover,
    &:focus-visible {
      transform: none;
    }
  }
`;

const SecondaryButton = styled(CtaButton)`
  background: var(--planning-button-bg);
  color: var(--planning-accent-soft);
  border-color: var(--planning-border);
  box-shadow: none;

  &:hover,
  &:focus-visible {
    background: var(--planning-card-hover);
    border-color: var(--planning-border-strong);
    color: var(--planning-heading);
    box-shadow: none;
  }

  svg {
    background: var(--planning-button-bg);
  }
`;

const SidebarArt = styled.svg`
  grid-column: 2;
  grid-row: 2 / 4;
  width: 100%;
  height: 100%;
  min-height: 390px;
  border-radius: 8px;
  border: 1px solid var(--planning-border-strong);
  background: linear-gradient(
    180deg,
    var(--planning-panel) 0%,
    var(--planning-card-strong) 100%
  );
  box-shadow:
    0 22px 48px var(--planning-shadow),
    inset 0 1px 0 var(--planning-border-muted),
    inset 0 -1px 0 var(--planning-border-muted);
  overflow: hidden;
  backdrop-filter: blur(12px);

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    display: none;
  }
`;

export default function PlanningArticlePage() {
  const { slug } = useParams<{ slug?: string }>();
  const { i18n, lang } = useI18n();
  const { mode } = useTheme();
  const article = useMemo(
    () => getPlanningArticleBySlug(slug, i18n),
    [i18n, slug],
  );
  const articleMenuLabels =
    i18n.PLANNING_ARTICLE_MENU as PlanningArticleMenuLabels;
  const [relatedIndex, setRelatedIndex] = useState(0);
  const [visibleRelatedCount, setVisibleRelatedCount] = useState(3);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedMenuSection, setSelectedMenuSection] =
    useState<PlanningArticleSectionId>(
      PLANNING_ARTICLE_DEFAULT_MENU_SECTION_ID,
    );
  const selectedMenuSectionRef = useRef<PlanningArticleSectionId>(
    PLANNING_ARTICLE_DEFAULT_MENU_SECTION_ID,
  );
  const scrollTargetsRef = useRef<Map<PlanningArticleSectionId, HTMLElement>>(
    new Map(),
  );
  const sectionBoundsRef = useRef<
    {
      sectionId: PlanningArticleSectionId;
      top: number;
      bottom: number;
    }[]
  >([]);
  const relatedLength = article?.relatedArticles.length ?? 0;
  const direction: PageDirection = lang === 'he' ? 'rtl' : 'ltr';
  const articleMenuSections = useMemo(() => {
    if (!article) return [];

    const sections: { id: PlanningArticleSectionId; label: string }[] = [
      {
        id: PLANNING_ARTICLE_INTRO_SECTION_ID,
        label: article.title || articleMenuLabels.essentials,
      },
      {
        id: 'planning-article-work',
        label: articleMenuLabels.work,
      },
    ];

    if (article.relatedArticles.length) {
      sections.push({
        id: 'planning-article-related',
        label: articleMenuLabels.related,
      });
    }

    if (article.faqs.length) {
      sections.push({
        id: 'planning-article-faq',
        label: articleMenuLabels.faq,
      });
    }

    sections.push({
      id: 'planning-article-author',
      label: articleMenuLabels.author,
    });

    return sections;
  }, [
    article,
    articleMenuLabels.author,
    articleMenuLabels.essentials,
    articleMenuLabels.faq,
    articleMenuLabels.related,
    articleMenuLabels.work,
  ]);

  const togglePlanningMenu = useCallback(() => {
    setIsMenuOpen(prev => !prev);
  }, []);

  const registerArticleSection = useCallback(
    (sectionId: PlanningArticleSectionId) => (element: HTMLElement | null) => {
      if (!element) {
        scrollTargetsRef.current.delete(sectionId);
        return;
      }

      scrollTargetsRef.current.set(sectionId, element);
    },
    [],
  );

  const scrollToArticleSection = useCallback(
    (sectionId: PlanningArticleSectionId) => {
      if (sectionId === PLANNING_ARTICLE_INTRO_SECTION_ID) {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });

        selectedMenuSectionRef.current = sectionId;
        setSelectedMenuSection(sectionId);
        return;
      }

      const element =
        scrollTargetsRef.current.get(sectionId) ??
        document.getElementById(sectionId);
      if (!element) return;

      scrollTargetsRef.current.set(sectionId, element);

      const top = element.getBoundingClientRect().top + window.pageYOffset;
      const isDesktopNow = window.innerWidth >= mediumScreenBreakpoint;
      const offset = isDesktopNow
        ? HEADER_HEIGHT + SEPARATOR_HEIGHT.largeScreen
        : SEPARATOR_HEIGHT.smallScreen;

      window.scrollTo({
        top: Math.max(0, top - offset),
        behavior: 'smooth',
      });

      selectedMenuSectionRef.current = sectionId;
      setSelectedMenuSection(sectionId);
    },
    [],
  );

  useEffect(() => {
    return scheduleSpaReadyAfterPaint();
  }, []);

  useEffect(() => {
    selectedMenuSectionRef.current = selectedMenuSection;
  }, [selectedMenuSection]);

  useEffect(() => {
    if (!articleMenuSections.length) return;

    if (
      !articleMenuSections.some(section => section.id === selectedMenuSection)
    ) {
      selectedMenuSectionRef.current = PLANNING_ARTICLE_DEFAULT_MENU_SECTION_ID;
      setSelectedMenuSection(PLANNING_ARTICLE_DEFAULT_MENU_SECTION_ID);
    }
  }, [articleMenuSections, selectedMenuSection]);

  useEffect(() => {
    const updateVisibleCount = () => {
      const isMobile = window.matchMedia(
        `(max-width: ${MEDIA_BREAKPOINTS.mediumScreen})`,
      ).matches;
      setVisibleRelatedCount(isMobile ? 1 : 3);
    };

    updateVisibleCount();
    window.addEventListener('resize', updateVisibleCount);
    return () => window.removeEventListener('resize', updateVisibleCount);
  }, []);

  useEffect(() => {
    setRelatedIndex(index =>
      Math.min(index, Math.max(0, relatedLength - visibleRelatedCount)),
    );
  }, [relatedLength, visibleRelatedCount]);

  useEffect(() => {
    if (!articleMenuSections.length) return;

    let raf: number | null = null;
    let measureRaf: number | null = null;

    const measureSections = () => {
      measureRaf = null;
      sectionBoundsRef.current = articleMenuSections
        .map(section => {
          const element = scrollTargetsRef.current.get(section.id);
          if (!element) return null;

          const rect = element.getBoundingClientRect();
          const top = rect.top + window.scrollY;

          return {
            sectionId: section.id,
            top,
            bottom: top + rect.height,
          };
        })
        .filter((section): section is NonNullable<typeof section> =>
          Boolean(section),
        )
        .sort((a, b) => a.top - b.top);
    };

    const scheduleMeasure = () => {
      if (measureRaf != null) return;
      measureRaf = window.requestAnimationFrame(measureSections);
    };

    const updateSelectedSection = () => {
      raf = null;

      if (!sectionBoundsRef.current.length) {
        measureSections();
      }

      const probeY = window.scrollY + window.innerHeight / 2;
      let current = selectedMenuSectionRef.current;

      for (const section of sectionBoundsRef.current) {
        if (probeY >= section.top && probeY < section.bottom) {
          current = section.sectionId;
          break;
        }
      }

      if (current !== selectedMenuSectionRef.current) {
        selectedMenuSectionRef.current = current;
        setSelectedMenuSection(current);
      }
    };

    const scheduleSelectedUpdate = () => {
      if (raf != null) return;
      raf = window.requestAnimationFrame(updateSelectedSection);
    };

    const handleScroll = () => {
      scheduleSelectedUpdate();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', scheduleMeasure, { passive: true });
    window.addEventListener('orientationchange', scheduleMeasure);

    scheduleMeasure();
    scheduleSelectedUpdate();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', scheduleMeasure);
      window.removeEventListener('orientationchange', scheduleMeasure);

      if (raf != null) {
        window.cancelAnimationFrame(raf);
      }
      if (measureRaf != null) {
        window.cancelAnimationFrame(measureRaf);
      }
    };
  }, [articleMenuSections]);

  if (!article) return <Navigate to="/" replace />;

  const maxRelatedIndex = Math.max(
    0,
    article.relatedArticles.length - visibleRelatedCount,
  );
  const safeRelatedIndex = Math.min(relatedIndex, maxRelatedIndex);
  const visibleRelatedArticles = article.relatedArticles.slice(
    safeRelatedIndex,
    safeRelatedIndex + visibleRelatedCount,
  );
  const moveRelated = (nextIndex: number) => {
    const index = Math.max(0, Math.min(maxRelatedIndex, nextIndex));
    setRelatedIndex(index);
  };

  if (article.slug !== normalizeSlugParam(slug)) {
    return <Navigate to={getPlanningArticlePath(article.slug)} replace />;
  }

  const phoneHref = `tel:${BUSINESS_BASE.phone.replace(/[\s-]/g, '')}`;
  const whatsappHref = i18n.buildWhatsAppWithText(
    i18n.PLANNING_WHATSAPP_TEXT(article.title),
  );
  const parentHref = getPlanningArticleParentHref(article);
  const WhatsappIcon = ICONS.whatsapp;
  const PhoneIcon = ICONS.phone;
  const parentContext = i18n.PLANNING_PARENT_CONTEXT(
    article.title,
    article.parentServiceLabel,
  );
  const footerRegion = article.locationCity
    ? (findRegionByCity(String(article.locationCity)) ?? 'מרכז')
    : 'מרכז';
  const nextControlSide = direction === 'rtl' ? 'left' : 'right';
  const previousControlSide = direction === 'rtl' ? 'right' : 'left';
  const nextControlSymbol = direction === 'rtl' ? '←' : '→';
  const previousControlSymbol = direction === 'rtl' ? '→' : '←';
  const planningThemeVariant: PlanningThemeVariant =
    article.service === SEO_SERVICE_HENNA ? 'henna' : 'wedding';
  return (
    <>
      <PlanningGlobalStyle $mode={mode} $variant={planningThemeVariant} />
      <Page $dir={direction} $mode={mode} $variant={planningThemeVariant}>
        <MenuDesktop
          sections={articleMenuSections}
          selectedSection={selectedMenuSection}
          setSelectedSection={setSelectedMenuSection}
          scrollToSection={scrollToArticleSection}
          variant="planning"
        />
        <MenuMobile
          sections={articleMenuSections}
          isMenuOpen={isMenuOpen}
          toggleMenu={togglePlanningMenu}
          selectedSection={selectedMenuSection}
          scrollToSection={scrollToArticleSection}
          variant="planning"
        />

        <Main>
          <Hero
            id={PLANNING_ARTICLE_INTRO_SECTION_ID}
            ref={registerArticleSection(PLANNING_ARTICLE_INTRO_SECTION_ID)}
            aria-labelledby="article-title"
          >
            <HeroText>
              <Eyebrow>
                {i18n.PLANNING_ARTICLE_EYEBROW(article.serviceDisplayLabel)}
              </Eyebrow>
              <Title id="article-title">{article.title}</Title>
              <Lead>{article.seoDescription ?? article.description}</Lead>
            </HeroText>

            <HeroImage
              src={article.image}
              srcSet={article.imageSrcSet}
              sizes={article.imageSizes}
              alt={article.imageAlt}
              loading="eager"
              decoding="async"
            />
          </Hero>

          <Body>
            <PrimaryPanel
              id="planning-article-essentials"
              ref={registerArticleSection('planning-article-essentials')}
            >
              <h2>{article.sectionTitle}</h2>
              <p>{article.intro}</p>
              <ul>
                {article.bullets.map(bullet => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
              <ParentContextText>
                {parentContext.before}{' '}
                <Link to={parentHref}>{parentContext.linkLabel}</Link>{' '}
                {parentContext.after}
              </ParentContextText>
            </PrimaryPanel>

            <CtaPanel
              id="planning-article-contact"
              ref={registerArticleSection('planning-article-contact')}
              aria-label={i18n.PLANNING_CONTACT_TITLE}
            >
              <h2>{i18n.PLANNING_CONTACT_TITLE}</h2>
              <p>{article.cta}</p>
              <ActionRow>
                <CtaButton
                  href={whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <WhatsappIcon aria-hidden="true" focusable="false" />
                  {i18n.PLANNING_WHATSAPP_LABEL}
                </CtaButton>
                <SecondaryButton href={phoneHref}>
                  <PhoneIcon aria-hidden="true" focusable="false" />
                  {i18n.PLANNING_PHONE_LABEL}
                </SecondaryButton>
              </ActionRow>
            </CtaPanel>

            <TrustPanel
              id="planning-article-trust"
              ref={registerArticleSection('planning-article-trust')}
            >
              <h2>{article.trustTitle}</h2>
              {article.trustParagraphs.map(paragraph => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </TrustPanel>

            <SidebarArt
              viewBox="0 0 320 620"
              aria-hidden="true"
              focusable="false"
              preserveAspectRatio="none"
            >
              <defs>
                <linearGradient
                  id="planning-panel-fill"
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                >
                  <stop
                    offset="0%"
                    stopColor="var(--planning-heading)"
                    stopOpacity="0.035"
                  />
                  <stop
                    offset="50%"
                    stopColor="var(--planning-accent)"
                    stopOpacity="0.07"
                  />
                  <stop
                    offset="100%"
                    stopColor="var(--planning-panel-strong)"
                    stopOpacity="0.06"
                  />
                </linearGradient>
                <linearGradient
                  id="planning-line"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="0%"
                >
                  <stop
                    offset="0%"
                    stopColor="var(--planning-heading)"
                    stopOpacity="0"
                  />
                  <stop
                    offset="18%"
                    stopColor="var(--planning-accent)"
                    stopOpacity="0.34"
                  />
                  <stop
                    offset="50%"
                    stopColor="var(--planning-heading)"
                    stopOpacity="0.66"
                  />
                  <stop
                    offset="82%"
                    stopColor="var(--planning-accent)"
                    stopOpacity="0.34"
                  />
                  <stop
                    offset="100%"
                    stopColor="var(--planning-heading)"
                    stopOpacity="0"
                  />
                </linearGradient>
                <filter
                  id="planning-soft-glow"
                  x="-20%"
                  y="-20%"
                  width="140%"
                  height="140%"
                  colorInterpolationFilters="sRGB"
                >
                  <feGaussianBlur
                    in="SourceGraphic"
                    stdDeviation="3"
                    result="softGlow"
                  />
                  <feMerge>
                    <feMergeNode in="softGlow" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>
              <rect width="320" height="620" fill="url(#planning-panel-fill)" />
              <rect
                x="18"
                y="18"
                width="284"
                height="584"
                rx="10"
                fill="none"
                stroke="var(--planning-border-strong)"
                strokeOpacity="0.2"
              />
              <g opacity="0.42">
                <path
                  d="M42 88 H278"
                  fill="none"
                  stroke="url(#planning-line)"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                />
                <path
                  d="M58 124 H262"
                  fill="none"
                  stroke="var(--planning-heading)"
                  strokeOpacity="0.12"
                  strokeWidth="1"
                  strokeLinecap="round"
                />
                <path
                  d="M58 496 H262"
                  fill="none"
                  stroke="var(--planning-heading)"
                  strokeOpacity="0.12"
                  strokeWidth="1"
                  strokeLinecap="round"
                />
                <path
                  d="M42 532 H278"
                  fill="none"
                  stroke="url(#planning-line)"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                />
              </g>
              <g opacity="0.62">
                <rect
                  x="64"
                  y="274"
                  width="7"
                  height="72"
                  rx="3.5"
                  fill="var(--planning-accent)"
                  opacity="0.22"
                />
                <rect
                  x="84"
                  y="250"
                  width="7"
                  height="120"
                  rx="3.5"
                  fill="var(--planning-accent-soft)"
                  opacity="0.28"
                />
                <rect
                  x="104"
                  y="286"
                  width="7"
                  height="48"
                  rx="3.5"
                  fill="var(--planning-heading)"
                  opacity="0.18"
                />
                <rect
                  x="124"
                  y="232"
                  width="7"
                  height="156"
                  rx="3.5"
                  fill="var(--planning-accent)"
                  opacity="0.3"
                />
                <rect
                  x="144"
                  y="262"
                  width="7"
                  height="96"
                  rx="3.5"
                  fill="var(--planning-heading)"
                  opacity="0.2"
                />
                <rect
                  x="164"
                  y="244"
                  width="7"
                  height="132"
                  rx="3.5"
                  fill="var(--planning-accent-soft)"
                  opacity="0.28"
                />
                <rect
                  x="184"
                  y="286"
                  width="7"
                  height="48"
                  rx="3.5"
                  fill="var(--planning-heading)"
                  opacity="0.18"
                />
                <rect
                  x="204"
                  y="232"
                  width="7"
                  height="156"
                  rx="3.5"
                  fill="var(--planning-accent)"
                  opacity="0.3"
                />
                <rect
                  x="224"
                  y="250"
                  width="7"
                  height="120"
                  rx="3.5"
                  fill="var(--planning-accent-soft)"
                  opacity="0.28"
                />
                <rect
                  x="244"
                  y="274"
                  width="7"
                  height="72"
                  rx="3.5"
                  fill="var(--planning-accent)"
                  opacity="0.22"
                />
              </g>
              <path
                d="M36 310 C66 286 92 286 120 310 C150 336 174 336 202 310 C230 284 254 286 284 310"
                fill="none"
                stroke="url(#planning-line)"
                strokeWidth="3.5"
                strokeLinecap="round"
                filter="url(#planning-soft-glow)"
              />
              <path
                d="M52 382 C88 362 118 362 154 382 C194 404 228 400 268 374"
                fill="none"
                stroke="var(--planning-accent-soft)"
                strokeOpacity="0.28"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <image
                href={PLANNING_ART_LOGO_URL}
                x="42"
                y="246"
                width="236"
                height="72"
                opacity="1"
                style={{ filter: 'var(--planning-logo-filter)' }}
                preserveAspectRatio="xMidYMid meet"
              />
              <path
                d="M82 430 H238"
                fill="none"
                stroke="var(--planning-heading)"
                strokeOpacity="0.16"
                strokeWidth="1"
                strokeLinecap="round"
              />
            </SidebarArt>

            <WorkPanel
              id="planning-article-work"
              ref={registerArticleSection('planning-article-work')}
            >
              <h2>{article.workTitle}</h2>
              <ul>
                {article.workBullets.map(bullet => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
            </WorkPanel>

            {!!article.relatedArticles.length && (
              <RelatedPanel
                id="planning-article-related"
                ref={registerArticleSection('planning-article-related')}
              >
                <RelatedHeader>
                  <h2>{article.relatedTitle}</h2>
                </RelatedHeader>
                <RelatedCarousel>
                  {article.relatedArticles.length > visibleRelatedCount && (
                    <>
                      <RelatedControl
                        type="button"
                        $side={nextControlSide}
                        onClick={() => moveRelated(safeRelatedIndex + 1)}
                        disabled={safeRelatedIndex >= maxRelatedIndex}
                        aria-label={i18n.PLANNING_NEXT_LABEL}
                      >
                        {nextControlSymbol}
                      </RelatedControl>
                      <RelatedControl
                        type="button"
                        $side={previousControlSide}
                        onClick={() => moveRelated(safeRelatedIndex - 1)}
                        disabled={safeRelatedIndex === 0}
                        aria-label={i18n.PLANNING_PREVIOUS_LABEL}
                      >
                        {previousControlSymbol}
                      </RelatedControl>
                    </>
                  )}
                  <RelatedCardsGrid
                    $visible={visibleRelatedCount}
                    $dir={direction}
                  >
                    {visibleRelatedArticles.map(related => (
                      <RelatedCard
                        key={related.href}
                        to={related.href}
                        $dir={direction}
                      >
                        <img
                          src={related.image}
                          srcSet={related.imageSrcSet}
                          sizes={related.imageSizes}
                          alt={related.imageAlt}
                          loading="lazy"
                          decoding="async"
                        />
                        <strong>{related.title}</strong>
                        <span>{related.description}</span>
                      </RelatedCard>
                    ))}
                  </RelatedCardsGrid>
                </RelatedCarousel>
              </RelatedPanel>
            )}

            {!!article.faqs.length && (
              <FaqPanel
                id="planning-article-faq"
                ref={registerArticleSection('planning-article-faq')}
              >
                <h2>{article.faqTitle}</h2>
                <FaqList>
                  {article.faqs.map(faq => (
                    <FaqItem key={faq.q} $dir={direction}>
                      <summary>{faq.q}</summary>
                      <p>{faq.a}</p>
                    </FaqItem>
                  ))}
                </FaqList>
              </FaqPanel>
            )}

            <FullWidthPanel
              id="planning-article-author"
              ref={registerArticleSection('planning-article-author')}
            >
              <h2>{article.authorTitle}</h2>
              <p>{article.authorText}</p>
              <AuthorLinkText>
                <Link to={parentHref}>{article.parentLinkLabel}</Link>
              </AuthorLinkText>
            </FullWidthPanel>
          </Body>
        </Main>

        <Footer
          showPlanningParentLinks
          variant="planning"
          region={footerRegion}
          planningCity={article.locationCity}
          planningEventType={article.parentEventType}
        />
      </Page>
    </>
  );
}
